import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Card,
  CardHeader,
  Table,
  Input,
  Row,
  Col,
  Button,
  Container,
  Form,
  FormGroup,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  CardFooter,
  Modal,
  ModalBody,
} from "reactstrap";
import { postMethod } from "../../services/httpServices";
import Header from "components/Headers/Header";
import { btnCss } from "appConfig";
import { iconCss } from "appConfig";
import ReactPaginate from "react-paginate";
import AddCategory from "./add-category";
import FullPageLoader from "components/FullPageLoader/fullpageloader";

const PAGE_SIZE = 10;

const ProductCategoryList = () => {
  const [categories, setCategories] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [sortKey, setSortKey] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch data once
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await postMethod(
        "/api/Inventory/GetAllCategories",
        {},
        "post"
      );
      if (res?.data?.responseCode === "200") {
        setCategories(res.data.result || []);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Debounced Search Handler
  const handleSearch = useCallback((text) => {
    setSearchText(text?.toLowerCase());
    setCurrentPage(0);
  }, []);

  const handlePageClick = ({ selected: selectedPage }) => {
    setCurrentPage(selectedPage);
  };

  // Handle Sorting
  const handleSort = (key) => {
    const newOrder = sortKey === key && sortOrder === "asc" ? "desc" : "asc";
    setSortKey(key);
    setSortOrder(newOrder);
  };

  // Apply search and sorting
  const filteredAndSorted = useMemo(() => {
    let filtered = categories.filter(
      (item) =>
        item.categoryName.toLowerCase().includes(searchText) ||
        item.subCategoryName.toLowerCase().includes(searchText)
    );

    if (sortKey) {
      filtered.sort((a, b) => {
        const aValue = a[sortKey] ?? "";
        const bValue = b[sortKey] ?? "";
        if (sortOrder === "asc") return aValue > bValue ? 1 : -1;
        return aValue < bValue ? 1 : -1;
      });
    }

    return filtered;
  }, [categories, searchText, sortKey, sortOrder]);

  const paginatedData = useMemo(() => {
    const start = currentPage * PAGE_SIZE;
    return filteredAndSorted.slice(start, start + PAGE_SIZE);
  }, [filteredAndSorted, currentPage]);

  const closeModal = () => {
    setOpen(false);
    fetchData();
  };

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Modal isOpen={open} style={{ width: "80vw", maxWidth: "90vw" }}>
          <ModalBody style={{ padding: 0, width: "100%" }}>
            <AddCategory closeModal={closeModal} />
          </ModalBody>
        </Modal>
        <Card className="shadow">
          <CardHeader className="border-0 d-flex  justify-content-between align-items-center">
            <Form
              className="form-inline mr-3 d-md-flex ml-lg-auto"
              onSubmit={(e) => e.preventDefault()}
            >
              <FormGroup className="mb-0 navbar-search ">
                <InputGroup className="input-group-alternative">
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="fas fa-search" />
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    className="search-cls"
                    placeholder="Search"
                    type="text"
                    value={searchText}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </InputGroup>
              </FormGroup>
              &nbsp;&nbsp;
              <Button
                className="my-4"
                color="primary"
                onClick={() => setOpen(true)}
              >
                Add Category
              </Button>
              <Button
                color="primary"
                onClick={() => fetchData()}
                size="sm"
                style={btnCss}
                data-toggle="tooltip"
                title="Refresh"
              >
                <i className="fas fa-redo fa-lg" style={iconCss} />
              </Button>
            </Form>
          </CardHeader>

          {loading ? <FullPageLoader /> : null}

          <Table responsive>
            <thead className="thead-light">
              <tr>
                {[
                  { key: "categoryCode", label: "Category Code" },
                  { key: "categoryName", label: "Category Name" },
                  { key: "subCategoryCode", label: "Sub Category Code" },
                  { key: "subCategoryName", label: "Sub Category Name" },
                  { key: "gstEligible", label: "GST Eligible" },
                ].map((col) => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    style={{ cursor: "pointer" }}
                  >
                    {col.label}{" "}
                    {sortKey === col.key && (
                      <i
                        className={`fas fa-sort-${
                          sortOrder === "asc" ? "up" : "down"
                        }`}
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedData.length > 0 ? (
                paginatedData
                  .reduce((acc, item, index, array) => {
                    const subItems = array.filter(
                      (sub) => sub.categoryCode === item.categoryCode
                    );

                    if (!acc.some((row) => row.key === item.categoryCode)) {
                      acc.push({
                        key: item.categoryCode,
                        rows: subItems,
                      });
                    }

                    return acc;
                  }, [])
                  .map((group) =>
                    group.rows.map((item, index) => (
                      <tr key={item.id}>
                        {index === 0 && (
                          <>
                            <td
                              rowSpan={group.rows.length}
                              style={{
                                verticalAlign: "middle",
                                fontWeight: "bold",
                              }}
                            >
                              {item.categoryCode}
                            </td>
                            <td
                              rowSpan={group.rows.length}
                              style={{
                                verticalAlign: "middle",
                                backgroundColor: "#f8f9fe",
                              }}
                            >
                              {item.categoryName}
                            </td>
                          </>
                        )}
                        <td>{item.subCategoryCode}</td>
                        <td>{item.subCategoryName}</td>
                        <td
                          className="text-center"
                          style={{
                            color: item.gstEligible ? "green" : "red",
                          }}
                        >
                          {item.gstEligible ? (
                            <i class="far fa-check-circle"></i>
                          ) : (
                            <i className="fas fa-times"></i>
                          )}
                        </td>
                      </tr>
                    ))
                  )
              ) : (
                <tr>
                  <td colSpan={5} className="text-center">
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <CardFooter className="py-4">
            {paginatedData.length > 0 && (
              <Row>
                <Col lg="6">
                  <div>
                    <small>
                      <strong>Total Records : {paginatedData.length}</strong>
                    </small>
                  </div>
                </Col>

                <Col lg="6">
                  <nav aria-label="...">
                    <div className="float-right">
                      <ReactPaginate
                        previousLabel={"prev"}
                        nextLabel={"next"}
                        breakLabel={"..."}
                        breakClassName={"break-me"}
                        pageCount={Math.ceil(filteredAndSorted.length / 10)}
                        marginPagesDisplayed={1}
                        pageRangeDisplayed={2}
                        onPageChange={handlePageClick}
                        containerClassName={"paginationV2"}
                        subContainerClassName={"pages paginationV2"}
                        activeClassName={"active"}
                        forcePage={currentPage}
                      />
                    </div>
                  </nav>
                </Col>
              </Row>
            )}
          </CardFooter>
        </Card>
      </Container>
    </>
  );
};

export default ProductCategoryList;
